import { createFeature, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { TenantsActions } from './tenants.actions';
import { TenantDto } from './models/tenant.model';

export interface TenantsState extends EntityState<TenantDto> {
  query: string;
  filters: Record<string, unknown[]>;
  totalElements: number;
  currentPage: number;
  pageSize: number;
  error: string | null;
  loading: boolean;
  saving: boolean;
}

export const tenantsAdapter = createEntityAdapter<TenantDto>({
  selectId: (tenant: TenantDto) => tenant.tenantUuid,
});

export const initialState: TenantsState = tenantsAdapter.getInitialState({
  query: '',
  filters: {},
  totalElements: 0,
  currentPage: 0,
  pageSize: 20,
  error: null,
  loading: false,
  saving: false,
});

const reducer = createReducer(
  initialState,
  on(TenantsActions.setQuery, (state, { query }) => ({ ...state, query, loading: true })),
  on(TenantsActions.setFilters, (state, { filters }) => ({ ...state, filters, loading: true })),
  on(TenantsActions.load, (state, { filters }) => ({ ...state, filters, loading: true })),
  on(TenantsActions.init, (state) => ({ ...state, loading: true })),
  on(TenantsActions.loadSuccess, (state, { tenants }) =>
    tenantsAdapter.setAll([...tenants], { ...state, error: null, loading: false }),
  ),
  on(TenantsActions.loadPageSuccess, (state, { tenants, totalElements, page, size }) =>
    tenantsAdapter.setAll([...tenants], {
      ...state,
      totalElements,
      currentPage: page,
      pageSize: size,
      error: null,
      loading: false,
    }),
  ),
  on(TenantsActions.loadFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    loading: false,
  })),
  on(TenantsActions.createTenant, (state) => ({ ...state, saving: true })),
  on(TenantsActions.createTenantSuccess, (state, { tenant }) =>
    tenantsAdapter.addOne({ ...tenant }, { ...state, error: null, saving: false }),
  ),
  on(TenantsActions.createTenantFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    saving: false,
  })),
  on(TenantsActions.deleteTenant, (state) => ({ ...state, saving: true })),
  on(TenantsActions.deleteTenantSuccess, (state, { tenantId }) =>
    tenantsAdapter.removeOne(tenantId, { ...state, error: null, saving: false }),
  ),
  on(TenantsActions.deleteTenantFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    saving: false,
  })),
  on(TenantsActions.updateTenant, (state) => ({ ...state, saving: true })),
  on(TenantsActions.updateTenantSuccess, (state, { tenant }) =>
    tenantsAdapter.updateOne(
      { id: tenant.tenantUuid, changes: { ...tenant } },
      { ...state, error: null, saving: false },
    ),
  ),
  on(TenantsActions.updateTenantFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    saving: false,
  })),
  on(TenantsActions.createSubscription, (state) => ({ ...state, saving: true })),
  on(TenantsActions.createSubscriptionSuccess, (state, { tenantId, subscription }) => {
    const tenant = state.entities[tenantId];
    if (!tenant) return { ...state, saving: false };
    return tenantsAdapter.updateOne(
      { id: tenantId, changes: { subscriptions: [...tenant.subscriptions, subscription] } },
      { ...state, error: null, saving: false },
    );
  }),
  on(TenantsActions.createSubscriptionFailure, (state, { error }) => ({
    ...state,
    error: String(error),
    saving: false,
  })),
  on(TenantsActions.clear, () => initialState),
);

export const tenantsFeature = createFeature({
  name: 'tenants',
  reducer,
});
